<template>
  <teleport to="body">
    <v-navigation-drawer v-model="drawerOpen" location="right" temporary width="560">
      <div class="ncrm-privacy-notice-drawer pa-5 d-flex flex-column ga-4">
        <div class="d-flex align-start justify-space-between ga-3">
          <div>
            <div class="text-caption text-medium-emphasis">{{ purposeName }}</div>
            <h3 class="text-h6">{{ notice ? $t(`${copyKey}.versionTitle`, { version: notice.version }) : $t(`${copyKey}.newTitle`) }}</h3>
            <div class="d-flex ga-2 mt-1">
              <v-chip :color="statusColor">{{ $t(`${copyKey}.status.${notice ? notice.status : "draft"}`) }}</v-chip>
              <v-chip variant="outlined">{{ locale.toUpperCase() }}</v-chip>
            </div>
          </div>
          <v-btn icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="drawerOpen = false" />
        </div>

        <v-alert type="info" density="compact">{{ $t(`${copyKey}.disclaimer`) }}</v-alert>
        <v-alert v-if="readOnly" type="info" density="compact" icon="mdi-lock-outline">
          {{ $t(`${copyKey}.retiredPurpose`) }}
        </v-alert>
        <v-alert v-if="errorMessage" type="error" density="compact">{{ errorMessage }}</v-alert>

        <template v-if="editable">
          <v-textarea v-model="form.consentText" :label="$t(`${copyKey}.text`)" rows="8" auto-grow />
          <v-text-field v-model="form.noticeUrl" :label="$t(`${copyKey}.url`)" :hint="$t(`${copyKey}.urlHint`)"
            persistent-hint :rules="[urlRule]" maxlength="500" />
          <div class="d-flex flex-wrap ga-2">
            <v-btn color="primary" variant="flat" :loading="busy === 'save'" :disabled="!canSave || Boolean(busy)" @click="save">
              {{ $t(`${copyKey}.${notice ? "save" : "create"}`) }}
            </v-btn>
            <v-btn v-if="notice" variant="tonal" color="primary" :disabled="dirty || Boolean(busy)" @click="confirmPublish = true">
              {{ $t(`${copyKey}.publish`) }}
            </v-btn>
          </div>
          <p v-if="notice && dirty" class="text-caption text-medium-emphasis mb-0">{{ $t(`${copyKey}.saveBeforePublish`) }}</p>
        </template>

        <template v-else-if="notice">
          <div class="ncrm-privacy-notice-drawer__text">{{ notice.consent_text }}</div>
          <dl class="ncrm-privacy-notice-drawer__fields">
            <dt>{{ $t(`${copyKey}.url`) }}</dt>
            <dd>
              <a v-if="linkUrl" :href="linkUrl" target="_blank" rel="noopener noreferrer">{{ notice.notice_url }}</a>
              <span v-else>{{ notice.notice_url || "—" }}</span>
            </dd>
            <dt>{{ $t(`${copyKey}.publishedAt`) }}</dt>
            <dd>{{ formatDateTime(notice.published_at) }}</dd>
            <dt>{{ $t(`${copyKey}.hash`) }}</dt>
            <dd class="ncrm-privacy-notice-drawer__hash">{{ notice.consent_text_hash }}</dd>
          </dl>
          <v-alert v-if="notice.status !== 'draft'" type="info" density="compact">{{ $t(`${copyKey}.immutable`) }}</v-alert>
          <div v-if="canCreateNext && !readOnly">
            <v-btn color="primary" variant="tonal" prepend-icon="mdi-content-copy" :loading="busy === 'next'" @click="createNext">
              {{ $t(`${copyKey}.nextVersion`) }}
            </v-btn>
          </div>
        </template>
      </div>
    </v-navigation-drawer>

    <v-dialog :model-value="confirmPublish" max-width="520" persistent>
      <v-card v-if="confirmPublish">
        <v-card-title>{{ $t(`${copyKey}.confirm.title`, { version: notice?.version, locale: locale.toUpperCase() }) }}</v-card-title>
        <v-card-text>{{ $t(`${copyKey}.confirm.body`) }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="Boolean(busy)" @click="confirmPublish = false">{{ $t(`${copyKey}.cancel`) }}</v-btn>
          <v-btn color="primary" variant="flat" :loading="busy === 'publish'" @click="publish">{{ $t(`${copyKey}.confirm.action`) }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </teleport>
</template>

<script>
import { createNotice, nextNoticeVersion, publishNotice, safeNoticeUrl, updateNotice } from "./privacyNoticeApi.mjs";

const ERROR_KEYS = Object.freeze({
  privacy_notice_immutable: "immutable",
  privacy_notice_changed: "changed",
  privacy_notice_draft_exists: "draftExists",
  privacy_purpose_retired: "purposeRetired",
  privacy_not_found: "notFound",
  privacy_invalid_input: "invalid",
});
const HTTPS_URL = /^https:\/\/[^\s]+$/i;

export default {
  name: "NcrmPrivacyNoticeDrawer",
  props: {
    modelValue: { type: Boolean, default: false },
    // `null` deschide formularul de creare pentru perechea `purpose + locale`.
    notice: { type: Object, default: null },
    purposeId: { type: Number, default: null },
    purposeName: { type: String, default: "" },
    locale: { type: String, default: "ro" },
    // Versiunea următoare se poate crea numai când perechea nu are deja o ciornă.
    canCreateNext: { type: Boolean, default: false },
    // Scop retras: istoricul se consultă, dar nicio acțiune nu mai e posibilă.
    readOnly: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "saved"],
  data: () => ({ form: { consentText: "", noticeUrl: "" }, busy: null, errorMessage: "", confirmPublish: false }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace.notices.drawer"; },
    drawerOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    editable() { return !this.readOnly && (!this.notice || this.notice.status === "draft"); },
    statusColor() { return { draft: "info", published: "success", retired: undefined }[this.notice ? this.notice.status : "draft"]; },
    linkUrl() { return this.notice ? safeNoticeUrl(this.notice.notice_url) : null; },
    // Textul se compară exact: hash-ul backendului este al acestor bytes.
    dirty() {
      if (!this.notice) return true;
      return this.form.consentText !== this.notice.consent_text || (this.form.noticeUrl || null) !== (this.notice.notice_url || null);
    },
    canSave() {
      return this.dirty && Boolean(this.form.consentText.trim()) && this.urlRule(this.form.noticeUrl) === true;
    },
  },
  watch: {
    modelValue(open) { if (open) this.reset(); },
  },
  methods: {
    reset() {
      this.form = { consentText: this.notice?.consent_text || "", noticeUrl: this.notice?.notice_url || "" };
      this.busy = null; this.errorMessage = ""; this.confirmPublish = false;
    },
    urlRule(value) {
      if (!value) return true;
      return (value.length <= 500 && HTTPS_URL.test(value) && safeNoticeUrl(value) !== null) || this.$t(`${this.copyKey}.urlHint`);
    },
    // Succesul închide drawer-ul și lasă secțiunea să reîncarce; eroarea rămâne inline.
    async run(kind, request) {
      this.busy = kind; this.errorMessage = "";
      try {
        await request();
        this.confirmPublish = false;
        this.$emit("saved", kind);
        this.drawerOpen = false;
      } catch (error) {
        this.confirmPublish = false;
        this.errorMessage = this.$t(`${this.copyKey}.errors.${ERROR_KEYS[error?.response?.data?.code] || "generic"}`);
      } finally {
        this.busy = null;
      }
    },
    save() {
      const payload = { consentText: this.form.consentText, noticeUrl: this.form.noticeUrl };
      return this.run("save", () => (this.notice
        ? updateNotice(this.notice.id, payload)
        : createNotice({ purposeId: this.purposeId, locale: this.locale, ...payload })));
    },
    publish() { return this.run("publish", () => publishNotice(this.notice.id, this.notice.consent_text_hash)); },
    createNext() { return this.run("next", () => nextNoticeVersion(this.notice.id)); },
    formatDateTime(value) {
      const date = new Date(value);
      if (!value || Number.isNaN(date.getTime())) return "—";
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-notice-drawer { text-align: left; }
.ncrm-privacy-notice-drawer__text {
  white-space: pre-wrap; padding: 12px; border-radius: var(--app-radius-base);
  background: rgba(var(--v-theme-on-surface), 0.05); font-size: 14px; overflow-wrap: anywhere;
}
.ncrm-privacy-notice-drawer__fields {
  display: grid; grid-template-columns: minmax(110px, auto) minmax(0, 1fr); gap: 8px 16px; margin: 0; font-size: 14px;
}
.ncrm-privacy-notice-drawer__fields dt { color: rgba(var(--v-theme-on-surface), 0.7); }
.ncrm-privacy-notice-drawer__fields dd { margin: 0; overflow-wrap: anywhere; }
.ncrm-privacy-notice-drawer__hash { font-size: 12px; }
</style>
